import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, FileBarChart2 } from "lucide-react";
import { api } from "@/api/client";
import type { Report } from "../../../types";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { CreateReportForm, type ReportTemplateValues } from "./CreateReportForm";
import { ReportCard } from "./ReportCard";

interface ReportsLocationState {
  reportTemplate?: ReportTemplateValues;
}

/**
 * Builds a report create-form template from an immutable generated report.
 *
 * @param report Existing report to use as a template.
 * @return Prefilled report create values.
 */
function buildReportTemplateValues(report: Report): ReportTemplateValues {
  return {
    name: `Copy of ${report.name}`,
    sourceName: report.name,
    brand_ids: report.brand_ids,
    prompt_id: report.prompt_id,
    models: report.models,
    runs_per_model: report.runs_per_model,
    query_mode: report.query_mode,
  };
}

export function ReportsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [templateValues, setTemplateValues] = useState<ReportTemplateValues | undefined>();

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await api.reports.list();
      setReports(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    const state = location.state as ReportsLocationState | null;

    if (!state?.reportTemplate) return;

    setTemplateValues(state.reportTemplate);
    setShowForm(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  // Poll while any report is running
  useEffect(() => {
    const hasRunning = reports.some(r => r.status === "running");
    if (!hasRunning) return;
    const timer = setInterval(fetchReports, 5000);
    return () => clearInterval(timer);
  }, [reports]);

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this report? This cannot be undone.")) return;
    await api.reports.delete(id);
    fetchReports();
  };

  /**
   * Opens report creation with an existing report's immutable config prefilled.
   *
   * @param report Existing report to use as a template.
   * @return void
   */
  const handleUseTemplate = (report: Report): void => {
    setTemplateValues(buildReportTemplateValues(report));
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={() => { setTemplateValues(undefined); setShowForm(templateValues ? true : !showForm); }}>
          <Plus className="w-4 h-4 mr-1.5" />
          New Report
        </Button>
      </div>

      {showForm && (
        <div className="animate-fade-up">
          <CreateReportForm
            key={templateValues ? `template-${templateValues.sourceName}-${templateValues.prompt_id}` : "new-report"}
            initialValues={templateValues}
            onSuccess={(reportId) => {
              setTemplateValues(undefined);
              setShowForm(false);
              fetchReports();
              navigate(`/app/reports/${reportId}`);
            }}
            onCancel={() => { setTemplateValues(undefined); setShowForm(false); }}
          />
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-slate-100 rounded-lg animate-subtle-pulse" />
          ))}
        </div>
      ) : reports.length === 0 ? (
        <EmptyState
          icon={FileBarChart2}
          title="No reports yet"
          description="Create a report to synthesize cross-model brand sentiment into a shareable document."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {reports.map(report => (
            <ReportCard
              key={report.id}
              report={report}
              onDelete={() => handleDelete(report.id)}
              onUseTemplate={() => handleUseTemplate(report)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
